import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "../components/Reveal";

const content = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: August 2026",
    intro: "Swiftwire Payments (\"Swiftwire\", \"we\", \"us\") respects your privacy. This policy explains what information we collect through our website and how we use it.",
    sections: [
      {
        heading: "Information we collect",
        body: "When you contact us through our website, we collect the details you provide — such as your name, work email, company, country and message. We also collect standard technical information (such as browser type and pages visited) to help us understand how the site is used and to keep it secure.",
      },
      {
        heading: "How we use your information",
        body: "We use the information you provide to respond to your enquiry, understand your use case, and communicate with you about Swiftwire's products and services where relevant. We do not sell your personal information.",
      },
      {
        heading: "How we share information",
        body: "We do not share your personal information with third parties except with service providers who help us operate our website and business (under appropriate confidentiality obligations), or where required by law.",
      },
      {
        heading: "Data retention",
        body: "We keep enquiry information for as long as needed to handle your request and maintain a record of our correspondence, after which it is deleted or anonymised.",
      },
      {
        heading: "Security",
        body: "We apply modern security practices to protect the information we hold. No method of transmission over the internet is completely secure, but we work to protect your information against unauthorised access.",
      },
      {
        heading: "Your rights",
        body: "Depending on where you live, you may have rights to access, correct, or delete your personal information, or to object to how we use it. To exercise any of these rights, contact us through our website.",
      },
      {
        heading: "Changes to this policy",
        body: "We may update this policy as our services evolve. The latest version will always be published on this page with its revision date.",
      },
      {
        heading: "Contact",
        body: "Questions about this policy or your information? Reach us through the contact page on this website.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: August 2026",
    intro: "These terms govern your use of the Swiftwire Payments website. By using this website, you accept these terms.",
    sections: [
      {
        heading: "About this website",
        body: "This website provides information about Swiftwire Payments and the payment and money movement platform we are building. Content on this site is for general information only and does not constitute financial, legal or professional advice.",
      },
      {
        heading: "No offer of services",
        body: "Nothing on this website constitutes an offer to provide payment services in any jurisdiction. Product capabilities described on this site may be shown as planned or illustrative, and availability may vary by market and over time.",
      },
      {
        heading: "Acceptable use",
        body: "You agree not to misuse this website — including attempting to disrupt it, access it by automated means beyond normal browsing, or use its content for unlawful purposes.",
      },
      {
        heading: "Intellectual property",
        body: "The Swiftwire name, logo, and the content and design of this website belong to Swiftwire Payments. You may not reproduce or reuse them without our written permission.",
      },
      {
        heading: "Third-party content",
        body: "This site may reference third-party data (for example, publicly available currency reference rates). Such data is provided as-is by its source and is displayed for illustration only.",
      },
      {
        heading: "Disclaimers",
        body: "This website is provided \"as is\" without warranties of any kind. While we work to keep information accurate and current, we do not guarantee completeness or uninterrupted availability.",
      },
      {
        heading: "Limitation of liability",
        body: "To the extent permitted by law, Swiftwire Payments is not liable for any indirect or consequential loss arising from your use of this website.",
      },
      {
        heading: "Changes to these terms",
        body: "We may revise these terms from time to time. The current version will always be published on this page.",
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Last updated: August 2026",
    intro: "This policy explains how the Swiftwire Payments website uses cookies and similar technologies.",
    sections: [
      {
        heading: "What are cookies?",
        body: "Cookies are small text files stored on your device when you visit a website. They help websites function, stay secure, and understand how visitors use them.",
      },
      {
        heading: "Cookies we use",
        body: "We use a small number of cookies: essential cookies that keep the site working and secure, and analytics cookies that help us understand aggregate usage so we can improve the experience. We do not use cookies to sell your data or build advertising profiles.",
      },
      {
        heading: "Analytics",
        body: "We use privacy-conscious product analytics to understand which pages are useful and where visitors encounter problems. This information is aggregated and is not used to identify you personally.",
      },
      {
        heading: "Managing cookies",
        body: "Most browsers let you block or delete cookies through their settings. Blocking essential cookies may affect how the site functions. Blocking analytics cookies will not affect your ability to use the site.",
      },
      {
        heading: "Changes to this policy",
        body: "We may update this policy as the technologies we use change. The latest version will always appear on this page.",
      },
    ],
  },
  compliance: {
    title: "Compliance",
    updated: "Last updated: August 2026",
    intro: "Swiftwire Payments is building modern money movement infrastructure — and we believe trust is earned through controls, transparency and accountability, not slogans.",
    sections: [
      {
        heading: "Our approach",
        body: "Compliance is being designed into the Swiftwire platform from the start. Our product and operational processes are being built to support the requirements of modern, regulated financial services.",
      },
      {
        heading: "Licensing status",
        body: "Swiftwire Payments is working through the appropriate regulatory pathways for the markets we intend to serve. We do not claim licences, registrations or certifications before they are granted, and this page will be updated as our regulatory status develops.",
      },
      {
        heading: "Financial crime controls",
        body: "Our platform is being designed with know-your-customer (KYC), anti-money-laundering (AML) and transaction monitoring controls appropriate for a money movement business, so that the businesses built on Swiftwire can meet their own obligations.",
      },
      {
        heading: "Data protection",
        body: "We handle personal information in line with our Privacy Policy and design our systems around data minimisation, access control and encryption best practices.",
      },
      {
        heading: "Partners and customers",
        body: "We expect to work with regulated partners and to apply risk-based due diligence to the businesses we serve. If you are evaluating Swiftwire and need compliance documentation, contact our team and we will share what is available.",
      },
      {
        heading: "Contact",
        body: "For compliance-related questions, reach out through the contact page and your enquiry will be routed to the right team.",
      },
    ],
  },
};

const Legal = () => {
  const { slug } = useParams();
  const page = content[slug];
  if (!page) return <Navigate to="/" replace />;

  return (
    <div data-testid={`legal-${slug}-page`}>
      <section className="sw-grid-bg border-b border-[#E2E8F0] bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to="/" data-testid="legal-back-home" className="group inline-flex items-center gap-1.5 text-sm font-medium text-[#64748B] transition-colors hover:text-[#1E3A6E]">
              <ArrowLeft size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:-translate-x-0.5" />
              Back to home
            </Link>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Legal</p>
            <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl">{page.title}</h1>
            <p className="mt-3 text-sm text-[#94A3B8]">{page.updated}</p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#334155] md:text-lg">{page.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {page.sections.map((s, i) => (
              <Reveal key={s.heading} delay={Math.min(i * 0.04, 0.2)}>
                <div className="border-b border-[#F1F5F9] pb-10 last:border-0">
                  <h2 className="font-heading text-xl font-bold text-[#0F172A]">{s.heading}</h2>
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#334155] md:text-base">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 sm:p-8">
              <p className="text-sm leading-relaxed text-[#334155]">
                Questions about this page?{" "}
                <Link to="/contact" data-testid="legal-contact-link" className="font-semibold text-[#1E3A6E] hover:underline">
                  Contact the Swiftwire team
                </Link>{" "}
                and we'll be happy to help.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Legal;
